// Page: VideoTemplates
// Purpose: App page to browse and preview video templates with search, format
// filter, saved-only view, infinite scroll, and modal preview. Manages saved
// state per namespace and respects user plan level.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import VideoTemplateFilters from '../../components/templates/templates/VideoTemplateFilters';
import { Tooltip } from '../../components/ui/overlays/Tooltip';
import VideoTemplateGrid from '../../components/templates/templates/VideoTemplateGrid';
import VideoTemplateModal from '../../components/templates/templates/VideoTemplateModal';
import { getAllVideoTemplates, saveVideoTemplate, unsaveVideoTemplate, getSavedVideoTemplateIds } from '../../api/content/videoTemplatesApi';
import { useAuth } from '../../context/auth';
import { useNamespace } from '../../context/namespace';
import { useToast } from '../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../components/shared/Footer';
import { AlertCircle, Video, Search, X, Loader2, TrendingUp, Clock, DollarSign } from 'lucide-react';
import { VIDEO_TEMPLATES_PER_PAGE } from '../../data/limits/templateLimits';
import SEOHead from '../../components/shared/SEOHead';

// Add keyframe animation for fade-in
const fadeInAnimation = `
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;


// Video Template Search Component (matching StockVideoSearch exactly)
const VideoTemplateSearch = ({ 
  searchQuery, 
  setSearchQuery, 
  onSearch, 
  onClearSearch, 
  isLoading, 
  isBasicUser = false
}) => {
  const [inputValue, setInputValue] = useState(searchQuery);

  // Sync inputValue with searchQuery when searchQuery changes from outside
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  const clearSearch = () => {
    setInputValue('');
    if (onClearSearch) {
      onClearSearch();
    } else {
      setSearchQuery('');
      onSearch('');
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(inputValue);
    onSearch(inputValue);
  };

  return (
    <div className="relative mb-6">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 py-2.5 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Search video templates..."
            value={inputValue}
            onChange={handleSearchChange}
          />
        </div>
        <button
          type="submit"
          disabled={!inputValue.trim() || isLoading || inputValue.trim() === searchQuery.trim()}
          className={`px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors flex items-center gap-2 ${
            !inputValue.trim() || inputValue.trim() === searchQuery.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
              : isLoading
                ? 'bg-primary-500 text-white cursor-wait'
                : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <span>Search</span>
          )}
        </button>
        
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="px-3 py-2.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 flex items-center transition-colors"
            title="Clear search"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </form>
    </div>
  );
};

function VideoTemplates() {
  
  // Add the style tag for animations
  useEffect(() => {
    // Add the animation styles to the document head
    const styleTag = document.createElement('style');
    styleTag.innerHTML = fadeInAnimation;
    document.head.appendChild(styleTag);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedVideoTemplate, setSelectedVideoTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortMethod, setSortMethod] = useState('random');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  // Separate loading state to control only the search bar button/spinner
  const [searchBarLoading, setSearchBarLoading] = useState(false);
  const [showSavedVideoTemplates, setShowSavedVideoTemplates] = useState(false);
  const [savedVideoTemplateIds, setSavedVideoTemplateIds] = useState(new Set());
  const [savedVideoTemplatesLoaded, setSavedVideoTemplatesLoaded] = useState(false);
  const [loadingSavedVideoTemplates, setLoadingSavedVideoTemplates] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  // State for API data and pagination
  const [videoTemplates, setVideoTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [allVideoTemplatesLoaded, setAllVideoTemplatesLoaded] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  
  // Get auth context and toast
  const { user } = useAuth();
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };
  
  // Get namespace context
  const { namespaces, currentNamespace } = useNamespace();
  
  // Find the current namespace ID from the namespaces array
  const currentNamespaceObj = namespaces.find(ns => ns.name === currentNamespace);
  const currentNamespaceId = currentNamespaceObj?.id;
  
  // Get user's plan
  const userPlan = user?.subscriptionStatus?.currentPlanLevel || user?.planLevel || 'basic';
  const isPremiumUser = userPlan === 'premium';
  const isBasicUser = userPlan === 'basic';
  
  // Refs for scroll handling and API request tracking
  
  const lastLoadTimeRef = useRef(0);
  const apiRequestInProgressRef = useRef(false);
  const lastRequestParamsRef = useRef(null);
  const requestCountRef = useRef(0);
  const sentinelRef = useRef(null);
  
  // Add intersection observer refs  
  const loadingMoreRef = useRef(false);



  const getSearchType = (method) => {
    switch (method) {
      case 'random':
        return 'random';
      case 'newest':
        return 'newest';
      default:
        return 'random';
    }
  };

  // Load saved video template IDs
  const loadSavedVideoTemplateIds = async () => {
    if (!user || !currentNamespaceId || loadingSavedVideoTemplates) return;

    try {
      setLoadingSavedVideoTemplates(true);
      console.log(`[VideoTemplates] Fetching saved video templates for namespace: ${currentNamespace} (${currentNamespaceId})`);
      const savedIds = await getSavedVideoTemplateIds(currentNamespaceId);
      console.log(`[VideoTemplates] Found ${savedIds.length} saved video templates in namespace:`, savedIds);
      setSavedVideoTemplateIds(new Set(savedIds || []));
      setSavedVideoTemplatesLoaded(true);
    } catch (error) {
      console.error('Error loading saved video template IDs:', error);
      setSavedVideoTemplatesLoaded(true);
      setSavedVideoTemplateIds(new Set());
    } finally {
      setLoadingSavedVideoTemplates(false);
    }
  };

  // Initial load of saved video templates
  useEffect(() => {
    if (user && currentNamespaceId) {
      loadSavedVideoTemplateIds();
    } else {
      // Clear saved video templates when logged out or no namespace
      setSavedVideoTemplateIds(new Set());
      setSavedVideoTemplatesLoaded(true);
    }
  }, [user, currentNamespace, currentNamespaceId]);

  // Load video templates from API
  const loadVideoTemplates = useCallback(async (resetList = false) => {
    console.log(`[VideoTemplates] Starting load - Reset: ${resetList}, Page: ${page}, Loading: ${loading}, AllLoaded: ${allVideoTemplatesLoaded}`);
    
    // Handle reset first - this clears blocking states
    if (resetList) {
      setPage(1);
      setVideoTemplates([]);
      setAllVideoTemplatesLoaded(false);
      setHasMore(true);
      setError(null);
      setContentReady(false);
      // Reset API tracking
      apiRequestInProgressRef.current = false;
      lastRequestParamsRef.current = null;
    }
    
    // Only check blocking conditions if not resetting
    if (!resetList && (loading || allVideoTemplatesLoaded)) {
      console.log('[VideoTemplates] ❌ Skipping load - already loading or all loaded');
      return;
    }

    // Prevent duplicate API calls
    const currentParams = JSON.stringify({
      page: resetList ? 1 : page,
      search: searchQuery,
      tags: Array.isArray(activeFilter) ? activeFilter.join(',') : (activeFilter || ''),
      sortBy: sortMethod
    });
    
    if (!resetList && apiRequestInProgressRef.current && lastRequestParamsRef.current === currentParams) {
      console.log('[VideoTemplates] ❌ Duplicate request prevented');
      return;
    }

    // Rate limiting - prevent requests more frequent than 500ms (but allow resets)
    const now = Date.now();
    if (!resetList && now - lastLoadTimeRef.current < 500) {
      console.log('[VideoTemplates] ❌ Rate limited');
      return;
    }

    apiRequestInProgressRef.current = true;
    lastRequestParamsRef.current = currentParams;
    requestCountRef.current++;
    
    const requestId = requestCountRef.current;
    console.log(`[VideoTemplates] 🚀 Starting API request #${requestId}`);

    setLoading(true);

    try {
      // Load saved video template IDs if needed
      if (showSavedVideoTemplates && !savedVideoTemplatesLoaded && currentNamespaceId) {
        await loadSavedVideoTemplateIds();
      }

      const requestOptions = {
        page: resetList ? 1 : page,
        limit: VIDEO_TEMPLATES_PER_PAGE,
        search: searchQuery,
        tags: Array.isArray(activeFilter) ? activeFilter.join(',') : (activeFilter || ''),
        sortBy: sortMethod
      };
      
      console.log(`[VideoTemplates] 📡 API Request #${requestId} with options:`, requestOptions);
      
      const result = await getAllVideoTemplates(requestOptions);
      
      // Check if this is still the latest request
      if (requestCountRef.current !== requestId) {
        console.log(`[VideoTemplates] ⚠️ Request #${requestId} is stale, ignoring results`);
        return;
      }
      
      // Validate API response structure
      if (!result || typeof result !== 'object') {
        console.error(`[VideoTemplates] ❌ Invalid API response structure:`, result);
        setError('Invalid response from server. Please try again.');
        return;
      }
      
      console.log(`[VideoTemplates] ✅ Received ${result.videoTemplates?.length || 0} video templates`);
      
      if (result.videoTemplates && Array.isArray(result.videoTemplates) && result.videoTemplates.length > 0) {
        let newVideoTemplates = result.videoTemplates;

        // Filter by saved video templates if needed
        if (showSavedVideoTemplates) {
          newVideoTemplates = newVideoTemplates.filter(template => savedVideoTemplateIds.has(template.id));
        }

        if (resetList) {
          setVideoTemplates(newVideoTemplates);
        } else {
          // Use functional update to avoid dependency on videoTemplates
          setVideoTemplates(prev => {
            const existingIds = new Set(prev.map(template => template.id));
            const uniqueNewTemplates = newVideoTemplates.filter(template => !existingIds.has(template.id));
            
            if (uniqueNewTemplates.length > 0) {
              return [...prev, ...uniqueNewTemplates];
            }
            return prev;
          });
          
          // Always increment page when not resetting, as we fetched a new page
          setPage(prev => prev + 1);
        }
        
        // Check if we've loaded all video templates
        if (result.videoTemplates.length < VIDEO_TEMPLATES_PER_PAGE) {
          console.log('[VideoTemplates] All video templates loaded (less than limit returned)');
          setHasMore(false);
          setAllVideoTemplatesLoaded(true);
        } else {
          setHasMore(true);
        }
      } else {
        console.log('[VideoTemplates] No video templates returned');
        if (resetList) {
          setVideoTemplates([]);
        }
        setHasMore(false);
        setAllVideoTemplatesLoaded(true);
      }
      
      setIsDataReady(true);
      
    } catch (err) {
      console.error('[VideoTemplates] ❌ Error loading video templates:', err);
      setError('Failed to load video templates. ' + (err.message || ''));
    } finally {
      setLoading(false);
      setInitialLoading(false);
      setSearchLoading(false);
      setSearchBarLoading(false);
      apiRequestInProgressRef.current = false;
      lastLoadTimeRef.current = Date.now();
      
      if (resetList) {
        setPage(2); // Set to 2 for next load
      }
      
      // Set content ready after a brief delay to ensure rendering is complete
      setTimeout(() => {
        setContentReady(true);
      }, 50);
    }
  }, [page, searchQuery, activeFilter, sortMethod, loading, allVideoTemplatesLoaded, showSavedVideoTemplates, savedVideoTemplateIds, savedVideoTemplatesLoaded, currentNamespaceId]);

  // Function to load more video templates (for infinite scroll)
  const loadMoreVideoTemplates = useCallback(async () => {
    // Don't load more if:
    // 1. Already loading
    // 2. No more templates to load  
    // 3. Already got all templates
    // 4. API request is already in progress
    // 5. Currently loading more (from our local ref)
    if (
      loading || 
      !hasMore || 
      allVideoTemplatesLoaded || 
      apiRequestInProgressRef.current || 
      loadingMoreRef.current
    ) {
      console.log('[VideoTemplates] Skipping loadMore - conditions not met');
      return;
    }
    
    // Mark as loading more locally
    loadingMoreRef.current = true;
    
    console.log(`[VideoTemplates] 🔄 Loading more video templates`);
    
    try {
      await loadVideoTemplates(false);
    } catch (err) {
      console.error('[VideoTemplates] ❌ Error loading more video templates:', err);
      setError('Failed to load more video templates. ' + (err.message || ''));
    } finally {
      loadingMoreRef.current = false;
    }
  }, [loading, hasMore, allVideoTemplatesLoaded, loadVideoTemplates]);

  // Initial load when component mounts and saved templates are loaded
  useEffect(() => {
    // Only auto-load if we haven't loaded any data yet and saved templates are loaded
    if (!isDataReady && !loading && savedVideoTemplatesLoaded) {
      console.log('[VideoTemplates] 🚀 Initial load triggered');
      loadVideoTemplates(true);
    }
  }, [savedVideoTemplatesLoaded, isDataReady, loading]);

  // Load video templates when filters change (but not on initial mount)
  useEffect(() => {
    // Only load if we have already loaded templates initially (isDataReady = true)
    if (isDataReady) {
      console.log('[VideoTemplates] 🚀 useEffect triggered with:', {
        searchQuery,
        activeFilter,
        sortMethod,
        showSavedVideoTemplates
      });
      console.log('[VideoTemplates] Filter change load effect triggered');
      loadVideoTemplates(true);
    }
  }, [searchQuery, activeFilter, sortMethod, showSavedVideoTemplates]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Only set up observer when content is ready and templates are loaded
    if (!isDataReady || !contentReady || videoTemplates.length === 0) {
      console.log('[VideoTemplates] 👁️ Not setting up observer - content not ready', {
        isDataReady,
        contentReady,
        videoTemplatesLength: videoTemplates.length
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        console.log('[VideoTemplates] 📏 Intersection event:', {
          isIntersecting: target.isIntersecting,
          hasMore,
          loading,
          allVideoTemplatesLoaded,
          apiRequestInProgress: apiRequestInProgressRef.current,
          loadingMore: loadingMoreRef.current
        });
        
        if (target.isIntersecting && hasMore && !loading && !allVideoTemplatesLoaded && !apiRequestInProgressRef.current) {
          console.log('[VideoTemplates] 📏 Sentinel element is visible, loading more video templates');
          loadMoreVideoTemplates();
        }
      },
      {
        root: null,
        rootMargin: '1500px 0px', // Load more when within 1500px of viewport bottom
        threshold: 0,
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
      console.log('[VideoTemplates] 👁️ Observer attached to sentinel element');
    } else {
      console.log('[VideoTemplates] ⚠️ Sentinel element not found, retrying in 100ms...');
      // Retry after a short delay to handle timing issues
      const retryTimer = setTimeout(() => {
        if (sentinelRef.current) {
          observer.observe(sentinelRef.current);
          console.log('[VideoTemplates] 👁️ Observer attached to sentinel element (retry)');
        } else {
          console.log('[VideoTemplates] ❌ Sentinel element still not found after retry');
        }
      }, 100);
      
      return () => {
        clearTimeout(retryTimer);
        if (sentinelRef.current) {
          observer.unobserve(sentinelRef.current);
        }
      };
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
        console.log('[VideoTemplates] 👁️ Observer detached from sentinel element');
      }
    };
  }, [loadMoreVideoTemplates, hasMore, loading, allVideoTemplatesLoaded, isDataReady, contentReady, videoTemplates.length]);

  const handleVideoTemplateClick = (videoTemplate) => {
    setSelectedVideoTemplate(videoTemplate);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideoTemplate(null);
  };

  const handleSearch = (query) => {
    console.log('[VideoTemplates] 🔍 handleSearch called with query:', query);
    // Only trigger loading state if the search query actually changed
    if (query !== searchQuery) {
      setSearchQuery(query);
      setSearchLoading(true);
      setSearchBarLoading(true);
      setContentReady(false);
    }
  };

  const handleClearSearch = () => {
    console.log('[VideoTemplates] 🔍 handleClearSearch called');
    setSearchQuery('');
    setSearchLoading(false);
    setSearchBarLoading(false);
  };

  const handleSortChange = (newSortMethod) => {
    console.log('[VideoTemplates] 🔀 handleSortChange called with:', newSortMethod);
    // Only trigger loading state if the sort method actually changed
    if (newSortMethod !== sortMethod) {
      setSortMethod(newSortMethod);
      setSearchLoading(true); // Show skeleton while new results load (grid-level only)
      setContentReady(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    console.log('[VideoTemplates] 🏷️ handleFilterChange called with:', newFilter);
    // Only trigger loading state if the filter value actually changed
    if (newFilter !== activeFilter) {
      setActiveFilter(newFilter);
      setSearchLoading(true); // Show skeleton while new results load (grid-level only)
      setContentReady(false);
    }
  };

  // Handle save/unsave video template
  const handleSaveVideoTemplate = async (videoTemplateId, shouldSave) => {
    if (!user) {
      return; // Silent failure
    }

    if (!currentNamespaceId) {
      return; // Silent failure
    }

    // Optimistically update UI first
    if (shouldSave) {
      setSavedVideoTemplateIds(prev => new Set([...prev, videoTemplateId]));
    } else {
      setSavedVideoTemplateIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(videoTemplateId);
        return newSet;
      });
    }

    try {
      if (shouldSave) {
        await saveVideoTemplate(videoTemplateId, currentNamespaceId);
      } else {
        await unsaveVideoTemplate(videoTemplateId, currentNamespaceId);
      }
      
      // If we're viewing saved video templates, refresh the list
      if (showSavedVideoTemplates) {
        loadVideoTemplates(true);
      }
    } catch (error) {
      console.error('Error saving/unsaving video template:', error);
      
      // Revert the UI change on error
      if (shouldSave) {
        setSavedVideoTemplateIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(videoTemplateId);
          return newSet;
        });
      } else {
        setSavedVideoTemplateIds(prev => new Set([...prev, videoTemplateId]));
      }
    }
  };

  const handleToggleSavedFilter = () => {
    // If trying to see saved video templates but no namespace is selected
    if (!showSavedVideoTemplates && !currentNamespaceId) {
      return; // Silent failure
    }
    
    const newValue = !showSavedVideoTemplates;
    setShowSavedVideoTemplates(newValue);
    setSearchLoading(true); // Show skeleton while new results load (grid-level only)
    setContentReady(false);
    
    // When turning off the saved filter, refresh saved video template IDs from the backend
    if (!newValue) {
      if (user && currentNamespaceId) {
        getSavedVideoTemplateIds(currentNamespaceId).then(savedIds => {
          console.log(`[VideoTemplates] Refreshing saved video template IDs when disabling saved filter:`, savedIds);
          setSavedVideoTemplateIds(new Set(savedIds || []));
        }).catch(err => {
          console.error('[VideoTemplates] Error refreshing saved video template IDs:', err);
          setSavedVideoTemplateIds(new Set());
        });
      } else {
        setSavedVideoTemplateIds(new Set());
      }
    }
  };

  // Calculate the number of columns for the skeleton based on screen width
  const getSkeletonColumns = () => {
    const width = window.innerWidth;
    if (width < 1024) return 2; // Below lg: 2 columns
    return 3; // lg and up: 3 columns
  };

  return (
    <div className="p-4 sm:p-6 pb-20 w-full max-w-[1600px] mx-auto">
      <SEOHead 
        pageType="app-video-templates"
      />
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Video Templates
            </h1>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <p>Browse and select from our collection of video templates</p>
            {user && (user.totalTemplatesEdited > 0 || user.totalVideoTemplatesEdited > 0) && (
              <Tooltip 
                text={
                  <div className="space-y-3">
                    <p className="font-medium">Your Stats</p>
                    <div className="flex items-center text-sm text-purple-200 mb-1">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      <span>You've used <b>{(user.totalTemplatesEdited || 0).toLocaleString()}</b> {(user.totalTemplatesEdited || 0) === 1 ? 'template' : 'templates'} and <b>{(user.totalVideoTemplatesEdited || 0).toLocaleString()}</b> video {(user.totalVideoTemplatesEdited || 0) === 1 ? 'template' : 'templates'} from Advocacy Lab</span>
                    </div>
                    <div className="flex items-center text-sm text-purple-200 mb-1">
                      <Clock className="mr-2 h-4 w-4" />
                      <span>You've saved <b>{(user.totalTimeSaved || 0).toLocaleString()}</b> {(user.totalTimeSaved || 0) === 1 ? 'hour' : 'hours'} of design time</span>
                    </div>
                    <div className="flex items-center text-sm text-purple-200">
                      <DollarSign className="mr-2 h-4 w-4" />
                      <span>You've saved <b>${(user.totalMoneySaved || 0).toLocaleString()}</b> in design costs</span>
                    </div>
                  </div>
                }
              >
                <AlertCircle className="ml-1.5 w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      
      {/* Search Component - on its own line */}
      <div className="mb-4">
        <VideoTemplateSearch 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery} 
          onSearch={handleSearch}
          onClearSearch={handleClearSearch}
          isLoading={searchBarLoading}
          isBasicUser={isBasicUser}
        />
      </div>
      
      {/* Filter Component - on its own line */}
      <div className="mb-6">
        <VideoTemplateFilters 
          activeFilter={activeFilter} 
          setActiveFilter={handleFilterChange} 
          sortMethod={sortMethod}
          setSortMethod={handleSortChange}
          searchLoading={searchLoading}
          isPremiumUser={isPremiumUser}
          showSavedVideoTemplates={showSavedVideoTemplates}
          setShowSavedVideoTemplates={handleToggleSavedFilter}
        />
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {/* Manual load button when data hasn't been loaded yet */}
      {!isDataReady && !initialLoading && !loading && (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center mb-4">
            <Video className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Ready to explore video templates?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mb-6">
            Click the button below to load our collection of professional video templates.
          </p>
          <button
            onClick={() => {
              setInitialLoading(true);
              loadVideoTemplates(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            Load Video Templates
          </button>
        </div>
      )}
      
      {/* Clean, centered loading indicator for initial loading OR active search/filter */}
      {(initialLoading || (loading && page === 1) || searchLoading || !contentReady) && (
        <div className="space-y-6">
          {/* Show skeleton grid immediately for better perceived performance */}
          <div className="masonry-wrapper">
            <div className="masonry-grid">
              {Array.from({ length: getSkeletonColumns() }, (_, columnIndex) => (
                <div key={`skeleton-column-${columnIndex}`} className="masonry-column" style={{ width: `${100 / getSkeletonColumns()}%` }}>
                  {[0, 1, 2, 3, 4].map(itemIndex => (
                    <div key={`skeleton-${columnIndex}-${itemIndex}`} className="masonry-item p-1">
                      <div className="masonry-item-inner bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse">
                        <div className="w-full aspect-square bg-gradient-to-b from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-lg"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Grid Component - show for all users when not in skeleton loading state */}
      {!initialLoading && !(loading && page === 1) && !searchLoading && contentReady && (
        <>
          {videoTemplates.length > 0 ? (
            <div className="animate-fade-in" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <VideoTemplateGrid
                videoTemplates={videoTemplates}
                onVideoTemplateClick={handleVideoTemplateClick}
                onSaveVideoTemplate={handleSaveVideoTemplate}
                savedVideoTemplateIds={savedVideoTemplateIds}
                loading={loading && page > 1}
                skeletonItems={[]}
                isPremiumUser={isPremiumUser}
              />
              
              {/* Sentinel element for infinite scroll */}
              {hasMore && !allVideoTemplatesLoaded && (
                <div 
                  ref={sentinelRef}
                  className="h-10 my-6 flex justify-center" 
                >
                  {loading && (
                    <div className="flex justify-center items-center py-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-100 dark:border-gray-800 border-b-purple-600 dark:border-b-purple-400"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Only show "No Video Templates Found" if we have confirmed data state and no templates
            isDataReady && (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-gray-600 dark:text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No Video Templates Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                  {searchQuery || activeFilter || showSavedVideoTemplates
                    ? 'Try adjusting your search or filter criteria to find video templates.'
                    : 'Our video template collection is being updated. Please check back soon for new templates.'}
                </p>
                {(searchQuery || activeFilter || showSavedVideoTemplates) && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveFilter(null);
                      setShowSavedVideoTemplates(false);
                    }}
                    className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )
          )}
        </>
      )}

      {/* Video Template Modal */}
      {isModalOpen && selectedVideoTemplate && (
        <VideoTemplateModal
          videoTemplate={selectedVideoTemplate}
          isOpen={isModalOpen}
          onClose={closeModal}
          isSaved={savedVideoTemplateIds.has(selectedVideoTemplate.id)}
          onSaveVideoTemplate={handleSaveVideoTemplate}
          isLoggedIn={!!user}
          userPlan={userPlan}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default VideoTemplates; 